import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ApiToken } from '../api/types';
import { Badge, Button, Card, CardHeader, ConfirmDialog, Dialog, EmptyState, ErrorBox, Field, Input, Loading, Notice, Select, Table, Td, Th, Tr, useToast } from './ui';
import { formatDate, formatDateTime } from '../lib/format';

/** Token-Liste mit Anlegen/Widerrufen – für "Mein Konto" und für Admins bei anderen Benutzern. */
export function TokenPanel({
  queryKey,
  list,
  create,
  revoke,
  description,
}: {
  queryKey: unknown[];
  list: () => Promise<{ data: ApiToken[] }>;
  create: (body: { name: string; expires_in_days: number | null }) => Promise<ApiToken & { token: string }>;
  revoke: (id: string) => Promise<unknown>;
  description?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);
  const q = useQuery({ queryKey, queryFn: list });
  const rm = useMutation({
    mutationFn: () => revoke(revoking!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
      toast('Token widerrufen');
      setRevoking(null);
    },
  });

  return (
    <Card>
      <CardHeader
        title="API-Tokens"
        description={description}
        actions={
          <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            Token erzeugen
          </Button>
        }
      />
      {q.isPending ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={q.error} className="m-4" />
      ) : q.data.data.length === 0 ? (
        <EmptyState title="Keine Tokens" icon={<KeyRound className="size-10" />} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Präfix</Th>
              <Th>Gültig bis</Th>
              <Th>Zuletzt benutzt</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {q.data.data.map((t) => (
              <Tr key={t.id} className={t.is_active ? undefined : 'text-slate-400'}>
                <Td className="font-medium">{t.name}</Td>
                <Td className="font-mono text-xs">{t.token_prefix}…</Td>
                <Td>{t.expires_at ? formatDate(t.expires_at) : 'unbegrenzt'}</Td>
                <Td className="text-xs">{t.last_used_at ? formatDateTime(t.last_used_at) : '–'}</Td>
                <Td>{t.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>{t.revoked_at ? 'Widerrufen' : 'Abgelaufen'}</Badge>}</Td>
                <Td className="text-right">
                  {t.is_active && (
                    <Button size="sm" variant="ghost" onClick={() => setRevoking(t)}>
                      Widerrufen
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      <CreateTokenDialog open={creating} onClose={() => setCreating(false)} create={create} onCreated={() => void qc.invalidateQueries({ queryKey })} />
      <ConfirmDialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title="Token widerrufen"
        message={`„${revoking?.name}“ funktioniert danach sofort nicht mehr.`}
        danger
        confirmLabel="Widerrufen"
        loading={rm.isPending}
        error={rm.error}
        onConfirm={() => rm.mutate()}
      />
    </Card>
  );
}

function CreateTokenDialog({
  open,
  onClose,
  create,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  create: (body: { name: string; expires_in_days: number | null }) => Promise<ApiToken & { token: string }>;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [days, setDays] = useState('365');
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (open) {
      setName('');
      setDays('365');
      setCreated(null);
      setCopied(false);
    }
  }, [open]);
  const m = useMutation({
    mutationFn: () => create({ name: name.trim(), expires_in_days: days ? Number(days) : null }),
    onSuccess: (t) => {
      setCreated(t.token);
      onCreated();
    },
  });

  if (created) {
    return (
      <Dialog open={open} onClose={onClose} title="Token erzeugt" footer={<Button variant="primary" onClick={onClose}>Fertig</Button>}>
        <Notice tone="warning">Der Token wird nur jetzt angezeigt. Bitte sofort sicher speichern (z. B. im Passwortmanager).</Notice>
        <div className="flex items-center gap-2">
          <code className="flex-1 rounded-md bg-slate-100 px-3 py-2 font-mono text-xs break-all">{created}</code>
          <Button
            icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            onClick={() => {
              void navigator.clipboard?.writeText(created).then(() => setCopied(true));
            }}
          >
            {copied ? 'Kopiert' : 'Kopieren'}
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="API-Token erzeugen"
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!name.trim()}>
            Erzeugen
          </Button>
        </>
      }
    >
      <Field label="Name" required hint="Wofür? z. B. „Inventur-Skript“">
        {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label="Gültigkeit">
        {(p) => (
          <Select {...p} value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="30">30 Tage</option>
            <option value="90">90 Tage</option>
            <option value="365">1 Jahr</option>
            <option value="">Unbegrenzt</option>
          </Select>
        )}
      </Field>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
